import { Component, OnDestroy } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, interval } from 'rxjs';
import { switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import {
  END_CLIENT_REPORT_UPLOAD_FORMATS,
  EndClientReportDataSource,
  EndClientReportDataSourceKind,
  EndClientReportStatus,
  EndClientReportUploadFile,
  EndClientReportUploadFormatOption,
  EndClientReportUploadProviderOption,
} from '../../models/end-client-reporting-package';
import { EndClientReportService } from './end-client-report.service';

/** Conversion is quick, and someone is watching the dialog while it runs. */
const CONVERSION_POLL_MS = 2000;

/**
 * Turning a survey export into a data source, in two modes: creating one, or replacing the file
 * behind an existing one. Shared rather than duplicated because both ask the same questions of the
 * same catalogue -- and because replacing a file is the upload equivalent of refreshing a pull, so
 * the two should not drift apart in what they accept.
 *
 * Replacing asks less: the source already knows its format and provider, so changing them would be
 * making a different source rather than re-syncing this one.
 */
@Component({
  selector: 'app-data-source-upload-modal',
  standalone: false,
  // The report's popup look -- see .ecr-modal in themes/end-client-reporting.scss.
  host: { class: 'ecr-modal' },
  template: `
    <div class="modal-header">
      <h5 class="modal-title nav-font16 font-weight-bold">
        {{ replacing ? 'Upload new data' : 'Add a data source' }}
      </h5>
      <button type="button" class="close" aria-label="Close" (click)="close()">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <div class="modal-body">
      @if (replacing) {
        <p class="grey5 nav-font14">
          Replaces the data behind <span class="font-weight-bold">{{ sourceName }}</span>. Every
          report reading it follows, and the copy it holds now is archived first.
        </p>
      } @else {
        <div class="form-group">
          <label class="nav-font12 font-weight-bold grey5" for="upload-format">What kind of file is it?</label>
          <select id="upload-format" class="form-control form-control-sm nav-font14"
                  [ngModel]="formatKey" (ngModelChange)="onFormatChange($event)">
            <option value="">Select one</option>
            @for (option of formats; track option.kind) {
              <option [value]="option.kind">{{ option.label }}</option>
            }
          </select>
        </div>

        <!-- Second, and only once the first is answered: the same platform's export in another
             format is a different parser, so what can be read depends on the pair. -->
        @if (format) {
          <div class="form-group">
            <label class="nav-font12 font-weight-bold grey5" for="upload-provider">Where is it from?</label>
            <select id="upload-provider" class="form-control form-control-sm nav-font14"
                    [ngModel]="providerKey" (ngModelChange)="onProviderChange($event)">
              <option value="">Select one</option>
              @for (option of supportedProviders(); track option.provider) {
                <option [value]="option.provider">{{ option.label }}</option>
              }
              <option value="other">Other</option>
            </select>
          </div>
        }

        @if (providerKey === 'other') {
          <p class="grey4 nav-font12">
            <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>
            That isn't supported yet. Contact your project manager to have the data added manually.
          </p>
        }
      }

      @if (provider) {
        @if (!replacing) {
          <div class="form-group">
            <label class="nav-font12 font-weight-bold grey5" for="upload-name">Name</label>
            <input id="upload-name" type="text" class="form-control form-control-sm nav-font14"
                   placeholder="e.g. Wave 2 — January"
                   [ngModel]="name" (ngModelChange)="name = $event">
            <span class="d-block grey4 nav-font12 mt-1">What reports will call this data.</span>
          </div>
        }

        @for (slot of provider.files; track slot.field) {
          <div class="form-group">
            <label class="nav-font12 font-weight-bold grey5" [for]="'upload-' + slot.field">{{ slot.label }}</label>
            <input type="file" class="form-control-file nav-font12" [id]="'upload-' + slot.field"
                   [accept]="slot.accept" (change)="onFileChosen(slot.field, $event)">
          </div>
        }

        @if (format?.kind === excelKind) {
          <p class="grey4 nav-font12 mb-0">
            Export the survey with its question list included — the workbook carries both the
            responses and the survey's questions and answers.
          </p>
        }
      }

      @if (watching) {
        <p class="grey4 nav-font12 mt-2">
          <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>Reading the file…
        </p>
      }
      @if (failure) {
        <p class="nav-font12 mt-2 upload-error">
          <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>{{ failure }}
        </p>
      }
    </div>

    <div class="modal-footer">
      <button type="button" class="btn btn-sm btn-link grey5" [disabled]="submitting" (click)="close()">Cancel</button>
      <button type="button" class="btn btn-sm btn-primary" [disabled]="!canUpload()" (click)="upload()">
        @if (submitting || watching) {
          <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>Uploading…
        } @else {
          <span>Upload</span>
        }
      </button>
    </div>
  `,
  styles: [`
    .upload-error {
      color: var(--nav-error);
    }
  `]
})
export class DataSourceUploadModalComponent implements OnDestroy {
  readonly formats = END_CLIENT_REPORT_UPLOAD_FORMATS;
  readonly excelKind = EndClientReportDataSourceKind.UploadedExcel;

  replacing = false;
  sourceName = '';
  name = '';
  submitting = false;
  watching = false;
  failure = '';

  formatKey = '';
  providerKey = '';
  format?: EndClientReportUploadFormatOption;
  provider?: EndClientReportUploadProviderOption;

  /** Called once the file has converted, so the opener can refresh whatever it is showing. */
  onUploaded?: (source: EndClientReportDataSource) => void;

  private projectId = '';
  private dataSourceId = '';
  private chosenFiles = new Map<string, File>();
  private destroy$ = new Subject<void>();

  constructor(private modal: BsModalRef, private reportService: EndClientReportService) { }

  configureCreate(projectId: string) {
    this.replacing = false;
    this.projectId = projectId;
  }

  /**
   * Format and provider come from the source rather than the form: changing either would make this
   * a different source, not new data for this one.
   */
  configureReplace(source: EndClientReportDataSource) {
    this.replacing = true;
    this.dataSourceId = source.id;
    this.sourceName = source.name;
    this.format = this.formats.find(f => f.kind === source.kind);
    this.provider = this.format?.providers.find(p => p.provider === source.provider);
    if (!this.provider) {
      this.failure = "This source's format isn't one that can be re-uploaded here.";
    }
  }

  onFormatChange(key: string) {
    this.formatKey = key;
    this.format = this.formats.find(f => String(f.kind) === key);
    // Answering the first question again invalidates the second.
    this.onProviderChange('');
  }

  onProviderChange(key: string) {
    this.providerKey = key;
    this.provider = this.supportedProviders().find(p => String(p.provider) === key);
    this.chosenFiles.clear();
    this.failure = '';
  }

  /** Only pairs with a converter. A format with none leaves the dropdown holding just "Other". */
  supportedProviders(): EndClientReportUploadProviderOption[] {
    return (this.format?.providers ?? []).filter(p => p.enabled);
  }

  onFileChosen(field: string, event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) this.chosenFiles.set(field, file);
    else this.chosenFiles.delete(field);
    this.failure = '';
  }

  canUpload(): boolean {
    return !this.submitting
      && !this.watching
      && !!this.provider
      && (this.replacing || this.name.trim().length > 0)
      && this.provider.files.every(slot => this.chosenFiles.has(slot.field));
  }

  upload() {
    if (!this.canUpload() || !this.provider) return;

    const files: EndClientReportUploadFile[] = this.provider.files
      .map(slot => ({ field: slot.field, file: this.chosenFiles.get(slot.field)! }));
    this.submitting = true;
    this.failure = '';

    const request = this.replacing
      ? this.reportService.reuploadDataSource(this.dataSourceId, this.provider.provider, files)
      : this.reportService.uploadDataSource(this.name.trim(), this.provider.provider, files, this.projectId);

    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: source => {
        this.submitting = false;
        this.watchUntilConverted(source.id);
      },
      error: err => {
        this.submitting = false;
        this.failure = err?.error?.message ?? "Couldn't upload that file.";
      }
    });
  }

  /**
   * The upload call only reports that the file was accepted. Conversion happens behind it, and a
   * file that can't be parsed fails seconds later — so the dialog stays open until the source
   * settles rather than closing on a success it doesn't yet know about.
   */
  private watchUntilConverted(dataSourceId: string) {
    this.watching = true;

    interval(CONVERSION_POLL_MS).pipe(
      switchMap(() => this.reportService.getDataSourceStatus(dataSourceId)),
      takeWhile(source => source.status === EndClientReportStatus.Ingesting, true),
      takeUntil(this.destroy$),
    ).subscribe({
      next: source => {
        if (source.status === EndClientReportStatus.Ingesting) return;
        this.watching = false;

        if (source.status === EndClientReportStatus.Failed) {
          this.failure = source.lastError
            ? `That file couldn't be read: ${source.lastError}`
            : "That file couldn't be read.";
          return;
        }

        this.onUploaded?.(source);
        this.close();
      },
      // A poll that can't reach the server says nothing about the file, so it doesn't claim to.
      error: () => {
        this.watching = false;
        this.failure = "Uploaded, but checking whether it could be read failed. Reload to see where it got to.";
      }
    });
  }

  close() {
    this.modal.hide();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
